import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Video, Calendar, Clock, ExternalLink, Search, X, Save, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherOnlineClasses() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(searchParams.get('action') === 'add');
  const [editClass, setEditClass] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '', description: '', course_id: '', subject_id: '',
    scheduled_date: '', scheduled_time: '', duration_minutes: 60,
    meeting_link: '', meeting_platform: 'zoom'
  });

  useEffect(() => { fetchData(); }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) { navigate('/staff-portal'); return; }
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [classesRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/online-classes`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setClasses(Array.isArray(classesRes) ? classesRes : []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.course_id || !formData.scheduled_date) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const url = editClass ? `${API_URL}/api/lms/online-classes/${editClass.id}` : `${API_URL}/api/lms/online-classes`;
      const response = await fetch(url, {
        method: editClass ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editClass ? 'Class updated!' : 'Class scheduled!');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await fetch(`${API_URL}/api/lms/online-classes/${id}`, { method: 'DELETE', headers: getHeaders() });
      toast.success('Class deleted!');
      fetchData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', course_id: '', subject_id: '', scheduled_date: '', scheduled_time: '', duration_minutes: 60, meeting_link: '', meeting_platform: 'zoom' });
    setEditClass(null);
  };

  const openEdit = (cls) => {
    setEditClass(cls);
    setFormData({
      title: cls.title || '', description: cls.description || '', course_id: cls.course_id || '',
      subject_id: cls.subject_id || '', scheduled_date: cls.scheduled_date || '', scheduled_time: cls.scheduled_time || '',
      duration_minutes: cls.duration_minutes || 60, meeting_link: cls.meeting_link || '', meeting_platform: cls.meeting_platform || 'zoom'
    });
    setShowModal(true);
  };

  const isUpcoming = (cls) => {
    const now = new Date();
    const classDate = new Date(`${cls.scheduled_date}T${cls.scheduled_time || '00:00'}`);
    return classDate > now;
  };

  const upcomingClasses = classes.filter(isUpcoming);
  const pastClasses = classes.filter(c => !isUpcoming(c));

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg"><ArrowLeft className="h-5 w-5" /></Link>
              <div><h1 className="text-xl font-bold">Online Classes</h1><p className="text-indigo-200 text-sm">Schedule and manage virtual classes</p></div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-white text-indigo-700 hover:bg-indigo-50"><Plus className="h-4 w-4 mr-2" />Schedule Class</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Upcoming Classes */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Video className="h-5 w-5 text-red-600" />Upcoming Classes ({upcomingClasses.length})</h2>
          {upcomingClasses.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">No upcoming classes scheduled</CardContent></Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingClasses.map(cls => (
                <Card key={cls.id} className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="p-2 bg-red-100 rounded-lg"><Video className="h-5 w-5 text-red-600" /></div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Upcoming</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{cls.title}</h3>
                    <p className="text-sm text-gray-500 mb-3">{cls.course_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{cls.scheduled_date}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{cls.scheduled_time}</span>
                    </div>
                    <div className="flex gap-2 pt-3 border-t">
                      {cls.meeting_link && (
                        <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button size="sm" className="w-full bg-red-600 hover:bg-red-700"><ExternalLink className="h-4 w-4 mr-1" />Join</Button>
                        </a>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(cls)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(cls.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Past Classes */}
        {pastClasses.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Past Classes ({pastClasses.length})</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pastClasses.slice(0, 6).map(cls => (
                <Card key={cls.id} className="opacity-75">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="p-2 bg-gray-100 rounded-lg"><Video className="h-5 w-5 text-gray-500" /></div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Completed</span>
                    </div>
                    <h3 className="font-medium text-gray-700">{cls.title}</h3>
                    <p className="text-xs text-gray-500">{cls.scheduled_date} {cls.scheduled_time}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Schedule Class Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editClass ? 'Edit Class' : 'Schedule Online Class'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Class title" required /></div>
              <div><Label>Description</Label><textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} /></div>
              
              <div><Label>Course *</Label>
                <Select value={formData.course_id} onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>{courses.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date *</Label><Input type="date" value={formData.scheduled_date} onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))} required /></div>
                <div><Label>Time *</Label><Input type="time" value={formData.scheduled_time} onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))} required /></div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Duration (min)</Label><Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))} /></div>
                <div><Label>Platform</Label>
                  <Select value={formData.meeting_platform} onValueChange={(v) => setFormData(prev => ({ ...prev, meeting_platform: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoom">Zoom</SelectItem>
                      <SelectItem value="google_meet">Google Meet</SelectItem>
                      <SelectItem value="teams">MS Teams</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div><Label>Meeting Link</Label><Input value={formData.meeting_link} onChange={(e) => setFormData(prev => ({ ...prev, meeting_link: e.target.value }))} placeholder="https://zoom.us/j/..." /></div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">{saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />{editClass ? 'Update' : 'Schedule'}</>}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
